import type { BoxSx } from "@modernatx-internal/component-library";
import { Box, Icon, Link, Target, Text } from "@modernatx-internal/component-library";
import { ModernaLogo } from "@modernatx-internal/component-library/logo/ModernaLogo";
import React from "react";

import type { FinderFooterBlockProps } from "./DesktopFooter";
import { EXPAND_ICON_ID } from "./DesktopFooter";

const THIS_YEAR = new Date().getFullYear();

const productsFooterLinkSx: BoxSx = {
  color: ["onSurface-brand-primary", "onSurface-brand-primary", "white"],
  transition: "transition-color-medium",
  ":hover": {
    textDecoration: "underline",
    textDecorationColor: ["onSurface-brand-primary", "onSurface-brand-primary", "white"]
  },
  ":focus-visible": {
    backgroundColor: "onSurface-brand-primary",
    borderRadius: "radius-4"
  },
  fontSize: "font-size-4"
};

const openCookieConsent = () => {
  if (window.MODERNA_COOKIE_CONSENT?.open) {
    window.MODERNA_COOKIE_CONSENT.open();
  }
};

const ACTIONS = {
  openCookieConsent
} as const;

interface FinderFooterContentProps extends FinderFooterBlockProps {
  expanded: boolean;
  handleExpandClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const FinderFooterContent: React.FC<FinderFooterContentProps> = ({
  expanded,
  handleExpandClick,
  locale = "en-US",
  mainContent,
  subSection
}) => {
  return (
    <>
      <Box
        sx={{
          alignItems: "flex-start",
          display: "flex",
          flexDirection: ["column", "column", "row"],
          gap: [10, 10, 0],
          justifyContent: "space-between"
        }}
      >
        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: ["column", "column", "row"]
          }}
        >
          {mainContent.map((section, i, arr) => (
            <React.Fragment key={`section-${i}`}>
              {handleExpandClick && i === 0 && (
                <Box
                  id={EXPAND_ICON_ID}
                  sx={{ px: "spacing-8", ":hover": { cursor: "pointer" } }}
                  onClick={handleExpandClick}
                >
                  <Icon
                    icon={expanded ? "caret-double-down" : "caret-double-up"}
                    size="sm"
                    sx={{ m: "spacing-3", color: "white" }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  // last item gets less right padding to avoid collision with logo and wrapping text
                  pr: arr.length - 1 === i ? "spacing-5" : "spacing-13",
                  flexShrink: 0
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    height: "spacing-9",
                    py: "spacing-4",
                    pt: ["spacing-9", "spacing-9", "spacing-4"]
                  }}
                >
                  <Text sx={{ fontWeight: "font-weight-bold" }}>{section.title}</Text>
                </Box>
                {section.links.map((link, j) => {
                  return (
                    <Box
                      key={`section-link-${j}`}
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "baseline",
                        py: ["spacing-2", "spacing-2", "spacing-4"]
                      }}
                    >
                      {link.title && (
                        <Text weight="bold" sx={{ flexShrink: 0 }}>
                          {link.title}
                        </Text>
                      )}
                      {link.action ? (
                        <Link
                          target={link.target}
                          sx={productsFooterLinkSx}
                          onClick={ACTIONS[link.action]}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "spacing-4",
                              fontVariant: "lining-nums tabular-nums"
                            }}
                          >
                            {link.text.map((textItem, k) => (
                              <Text key={`section-link-text-${k}`} lineHeight="leading-none">
                                {textItem}
                              </Text>
                            ))}
                          </Box>
                        </Link>
                      ) : (
                        <Link href={link.href} target={link.target} sx={productsFooterLinkSx}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "spacing-4",
                              fontVariant: "lining-nums tabular-nums"
                            }}
                          >
                            {link.text.map((textItem, k) => (
                              <Text key={`section-link-text-${k}`} lineHeight="leading-none">
                                {textItem}
                              </Text>
                            ))}
                          </Box>
                        </Link>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </React.Fragment>
          ))}
        </Box>
        {
          <Box
            sx={{
              alignItems: ["flex-start", "flex-start", "flex-end"],
              display: ["none", null, "flex"],
              flexDirection: "column",
              gap: [5, 8, 8],
              height: "spacing-9",
              justifyContent: "center"
            }}
          >
            <Target href={`https://www.modernatx.com/${locale}`}>
              <ModernaLogo sx={{ height: 8 }} />
            </Target>
          </Box>
        }
      </Box>

      <Box
        sx={{
          alignItems: ["flex-start", null, "center"],
          display: "flex",
          flexDirection: ["column", null, "row"],
          justifyContent: "space-between",
          pb: [0, null, "spacing-5"],
          pt: "spacing-9"
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: ["column", null, "row"],
            alignItems: ["flex-start", null, "center"]
          }}
        >
          {subSection?.links?.map((link, i) => (
            <Link
              key={`subsection-link-${i}`}
              href={link.href}
              target={link.target}
              sx={{
                ...productsFooterLinkSx,
                pr: "spacing-9"
              }}
            >
              {link.text.map((text, j) => (
                <Text key={`subsection-link-text-${j}`}>{text}</Text>
              ))}
            </Link>
          ))}
        </Box>
        <Text as="p" sx={{ fontVariant: "lining-nums tabular-nums", pt: [4, 4, 0] }}>
          © {THIS_YEAR} <Text as="span">{subSection?.documentId}</Text>
        </Text>
      </Box>
    </>
  );
};
